import styled from '@emotion/styled';
import { alpha, Typography } from '@mui/material';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MicroMutedIcon, VideoRecorderOffIcon } from 'assets/svg';
import { sendAnalyticsError } from 'features/analytics/helpers';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { isMicrophoneMutedSelector } from 'features/call/call-base/store';
import {
  callIdSelector,
  sipCallIdSelector,
} from 'features/call/call-base/store/selectors';
import { UserVideo } from 'features/local-video/components';
import { useLocalVideo } from 'features/local-video/hooks';
import { isLocalVideoEnabledSelector } from 'features/local-video/store';
import { useMediaStream } from 'features/media-streams/hooks';
import { getNetworkStats, isRtcConnectionActive } from 'features/sip/helpers';
import { useSipSession } from 'features/sip/hooks';
import { textEllipsis } from 'features/theme/commonCss';
import theme from 'features/theme/theme';
import { agentNumberSelector, userSelector } from 'features/user/store';
import {
  isLocalVideoPrivacyEnabledSelector,
  startLocalVideoPrivacyLoading,
  stopLocalVideoPrivacyLoading,
} from 'features/video-privacy/store';
import { selectCallPageStatus } from 'features/call/call-ui-state/store';

export const LocalVideo: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const rtcConnection = useSipSession();
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const { getVideoStream } = useMediaStream();
  const { setLocalVideoPrivacy, setLocalVideo } = useLocalVideo();

  const isLocalVideoPrivacyEnabled = useAppSelector(
    isLocalVideoPrivacyEnabledSelector
  );
  const isLocalVideoEnabled = useAppSelector(isLocalVideoEnabledSelector);
  const isUserMicrophoneMuted = useAppSelector(isMicrophoneMutedSelector);
  const agentNumber = useAppSelector(agentNumberSelector);
  const user = useAppSelector(userSelector);
  const callPageStatus = useAppSelector(selectCallPageStatus);
  const callId = useAppSelector(callIdSelector);
  const sipCallId = useAppSelector(sipCallIdSelector);
  const isPrivacyEnabled = useAppSelector(isLocalVideoPrivacyEnabledSelector);

  const handleLocalVideo = useCallback(
    async () => {
      dispatch(startLocalVideoPrivacyLoading());

      if (isLocalVideoPrivacyEnabled) {
        setMediaStream(null);
        if (rtcConnection && isRtcConnectionActive(rtcConnection)) {
          await setLocalVideoPrivacy(rtcConnection);
        }
      } else {
        if (!mediaStream) {
          const videoStream = await getVideoStream();
          setMediaStream(videoStream);
        }

        if (rtcConnection && isRtcConnectionActive(rtcConnection)) {
          await setLocalVideo(rtcConnection);
        }
      }

      dispatch(stopLocalVideoPrivacyLoading());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isLocalVideoPrivacyEnabled,
      isLocalVideoEnabled,
      rtcConnection,
      mediaStream,
    ]
  );

  const handleStalled = useCallback(async () => {
    sendAnalyticsError({
      name: 'Black Screen Error',
      event: {
        user,
        callId,
        sipCallId,
        callPageStatus,
        networkStats: await getNetworkStats(rtcConnection),
      },
    });
  }, [callId, callPageStatus, rtcConnection, sipCallId, user]);

  useEffect(() => {
    handleLocalVideo();
  }, [handleLocalVideo]);

  return (
    <>
      {isLocalVideoEnabled && (
        <Container
          isPrivacyEnabled={isPrivacyEnabled}
          data-testid='local-video'
        >
          {isLocalVideoPrivacyEnabled ? (
            <VideoRecorderOffIcon title='video-recorder-off-icon' />
          ) : (
            <UserVideo
              mediaStream={mediaStream}
              handleStalled={handleStalled}
            />
          )}
          <UserLabel variant='caption'>
            {t('me')} ({agentNumber})
            {isUserMicrophoneMuted && <MutedIcon title='micro-muted' />}
          </UserLabel>
        </Container>
      )}
    </>
  );
};

const Container = styled('div')<{ isPrivacyEnabled: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 266px;
  height: ${(props) => (props.isPrivacyEnabled ? '200px' : 'auto')};
  /* TODO: get from theme when Doug updates designs */
  background-color: #222;
  border-radius: 8px;
`;

const UserLabel = styled(Typography)`
  max-width: 100%;
  position: absolute;
  display: flex;
  bottom: ${theme.spacing(8)};
  left: ${theme.spacing(7)};
  padding: ${theme.spacing(3, 4)};
  border-radius: 8px;
  background-color: ${alpha(theme.palette.common.black, 0.4)};
  color: ${theme.palette.common.white};
  ${textEllipsis}
`;

const MutedIcon = styled(MicroMutedIcon)`
  width: 16px;
  height: 16px;
  margin-left: ${theme.spacing(2)};
`;
